import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { Button, Icons } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { PricingTableSkeleton } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-skeleton';
import { SearchSkeleton } from '@/experiences/sales-portal/components/search-skeleton';
import { Typography } from '@usepepper/ui/atoms/typography';

interface Props {
  alertMe: boolean;
  loadingState: number;
  onAlertMePress: () => void;
  phraseKeyPrefix: string;
  testID?: string;
}

export const PricingTableLoadingSkeleton: React.FC<Props> = ({
  alertMe,
  loadingState,
  onAlertMePress,
  phraseKeyPrefix,
  testID,
}) => {
  const [styles, theme] = useStyles();

  return (
    <View style={styles.loadingContent} testID={testID}>
      <SearchSkeleton />
      <PricingTableSkeleton />
      <View style={styles.loadingWrapper}>
        <View style={styles.loadingContentWrapper}>
          <View style={styles.rowWithGap}>
            <ActivityIndicator
              accessibilityLabel="loading"
              color={theme.sk.color.icon.secondary}
              size="small"
              style={styles.activityIndicator}
            />
            <Typography variant="paragraph-md">
              {i18n.t(`${phraseKeyPrefix}_${loadingState}`)}
            </Typography>
          </View>
          <Typography
            color={theme.sk.color.text.tertiary}
            variant="paragraph-sm"
          >
            {i18n.t('pricing.table.loading.description')}
          </Typography>
        </View>
        <Button
          accessibilityLabel={i18n.t(
            `pricing.table.loading.alert_me.${alertMe ? 'on' : 'off'}`,
          )}
          onPress={onAlertMePress}
          size="$sm"
          style={styles.autoAlignSelf}
          variant="secondary"
        >
          <View style={styles.rowWithGap}>
            {alertMe ? (
              <Icons.Bell color={theme.sk.color.icon.primary} size={12} />
            ) : (
              <Icons.BellOff color={theme.sk.color.icon.secondary} size={12} />
            )}
            <Typography
              color={
                alertMe
                  ? theme.sk.color.text.primary
                  : theme.sk.color.text.secondary
              }
              variant="label-sm"
            >
              {i18n.t(
                `pricing.table.loading.alert_me.${alertMe ? 'on' : 'off'}`,
              )}
            </Typography>
          </View>
        </Button>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  activityIndicator: {
    transform: [{ scale: 0.75 }],
  },
  autoAlignSelf: {
    alignSelf: 'auto',
  },
  loadingContent: {
    flex: 1,
    gap: 42,
  },
  loadingContentWrapper: {
    alignItems: 'center',
  },
  loadingWrapper: {
    alignItems: 'center',
    gap: theme.sk.spacing.spaceMedium,
    marginTop: theme.sk.spacing.space5xlarge,
  },
  rowWithGap: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
